import React, { useContext, useEffect, useRef, useState } from "react";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useTranslation } from "../../context/Internationalization";
import { AuthContext } from "../../context/AuthContext";
import {
  GET_USER_PROFILES_SA,
  SEARCH_USERS_FOR_IMPERSONATION_SA,
} from "./SuperAdminService";
import { Box, Button, Grid } from "@mui/material";
import boxStyles from "../../styles/boxing";
import Stack from "@mui/material/Stack";
import MainTitle from "../../Components/Common/MainTitle/MainTitle";
import Divider from "@mui/material/Divider";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import { GET_USER } from "../../graphql/userGraphql";
import ImpersonateConfirmationModal from "./UsersTable/ImpersonateConfirmationModal";
import { useSuperAdmin } from "../../context/SuperAdminContext";
import { getStorage } from "../../auth/utils";
import { useNavigate } from "react-router-dom";
import Form from "../../Components/Form/Form";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import useStyles from "../Forms/formsStyles";
import ProfilesTable from "./ProfilesTable/ProfilesTable";
import TeamsTable from "./TeamsTable/TeamsTable";
import UserInvitationsTable from "./UserInvitations/UserInvitationsTable";
import UserClubTable from "./UserClubTable/UserClubTable";
import { SnackBarContext } from "../../context/SnackBarContext";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { USER_ROLES } from "../../utility/Enum";
import {
  endImpersonationSession,
  setImpersonatedActiveClub,
  suspendActiveClubSelection,
} from "../../auth/impersonationClub";

const schema = (t) => [
  {
    name: "firstName",
    label: t("firstName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "lastName",
    label: t("lastName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "mobile",
    label: t("mobile"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "email",
    label: t("email"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
];

const initialState = {
  user: null,
  candidates: [],
  profiles: [],
  loading: false,
  impersonating: false,
  impersonateModal: false,
};

const SuperAdminImpersonate = (props) => {
  const { t, switchLanguage } = useTranslation();
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState({ id: "", identity: "" });
  const { setImpersonate } = useContext(AuthContext);
  const { setUser } = useSuperAdmin();
  const navigate = useNavigate();
  const styles = useStyles();
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const requestVersion = useRef(0);
  const impersonationPending = useRef(false);
  useEffect(() => () => { requestVersion.current += 1; }, []);

  const handleImpersonate = () => {
    setState({ ...state, impersonateModal: true });
  };

  const reportError = (error, operation, query = "SEARCH_USERS_FOR_IMPERSONATION_SA") => {
    errorLog(error, "SuperAdminImpersonate", operation, query);
    snackBar.setOptions({ alertMessage: error.message, alertSeverity: "error" });
    snackBar.setSnackbarOpen(true);
  };

  const loadSelectedUser = async (user, version) => {
    if (!user?._id) throw new Error(t("impersonation_missing_id"));
    const response = await axiosGraphQL({
      query: GET_USER_PROFILES_SA,
      variables: { userId: user._id },
    });
    if (version !== requestVersion.current) return;
    setState((previous) => ({
      ...previous,
      user,
      profiles: response?.data?.data?.getClubProfilesSA || [],
      loading: false,
    }));
  };

  const selectCandidate = async (user) => {
    if (impersonationPending.current) return;
    const version = ++requestVersion.current;
    setState((previous) => ({ ...previous, user: null, profiles: [], loading: true }));
    try {
      await loadSelectedUser(user, version);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setState((previous) => ({ ...previous, loading: false }));
      reportError(error, "selectCandidate", "GET_USER_PROFILES_SA");
    }
  };

  const resolveUser = async () => {
    if (impersonationPending.current) return;
    const version = ++requestVersion.current;
    setState({ ...initialState, loading: true });
    const userId = filters.id.trim();
    const identity = filters.identity.trim();
    if (!userId && !identity) {
      setState(initialState);
      return;
    }
    try {
      const response = await axiosGraphQL({
        query: SEARCH_USERS_FOR_IMPERSONATION_SA,
        variables: userId ? { userId } : { identity },
      });
      if (version !== requestVersion.current) return;
      const candidates = response?.data?.data?.searchUsersForImpersonationSA || [];
      if (userId && (candidates.length !== 1 || candidates[0]._id !== userId.toLowerCase())) {
        throw new Error(t("impersonation_identity_mismatch"));
      }
      if (!candidates.length) throw new Error(t("impersonation_no_matches"));
      setState({ ...initialState, candidates, loading: candidates.length === 1 });
      if (candidates.length === 1) await loadSelectedUser(candidates[0], version);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setState((previous) => ({ ...previous, loading: false }));
      reportError(error, "resolveUser");
    }
  };

  const onChangeFilter = ({ target: { name, value } }) => {
    if (impersonationPending.current) return;
    requestVersion.current += 1;
    setState(initialState);
    // Only one lookup mode is active; an ID is never translated into a phone lookup.
    setFilters({ id: "", identity: "", [name]: value });
  };

  const onImpersonate = async () => {
    if (impersonationPending.current || state.loading || !state.user?._id) return;
    const target = state.user;
    impersonationPending.current = true;
    setState((previous) => ({ ...previous, impersonating: true, impersonateModal: false }));
    const storage = getStorage();
    suspendActiveClubSelection(storage);
    try {
      setUser(target);
      const response = await axiosGraphQL({ query: GET_USER });
      const me = response?.data?.data?.me;
      if (me?.user?._id !== target._id) {
        throw new Error(t("impersonation_identity_mismatch"));
      }
      if (!me.club || !me.clubs?.length) {
        throw new Error(t("impersonation_no_club"));
      }

      const user = { ...me.user, club: me.club, clubs: me.clubs };
      if (user.language !== storage.getItem("language")) {
        switchLanguage(user.language);
      }
      setImpersonatedActiveClub(storage, user.club._id);
      storage.setItem("impersonate", JSON.stringify(user));
      setImpersonate(user);
      navigate("/");
    } catch (error) {
      endImpersonationSession(storage);
      setUser(null);
      setImpersonate(null);
      reportError(error, "onImpersonate", "GET_USER");
    } finally {
      impersonationPending.current = false;
      setState((previous) => ({ ...previous, impersonating: false }));
    }
  };

  const onCloseModal = () => {
    setState({ ...state, impersonateModal: false });
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Box>
        <ImpersonateConfirmationModal
          open={state.impersonateModal}
          onClose={onCloseModal}
          onImpersonate={onImpersonate}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={boxStyles.headerContainer} component="div">
              <Stack direction={"column"} spacing={1}>
                <MainTitle title={t("search_user")} />
              </Stack>
            </Box>
            <Divider style={{ width: "100%" }} />
          </Grid>
          <Grid item xs={12} style={boxStyles.filtersContainer}>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={1}
            >
              <Stack direction={"row"} spacing={1} alignItems={"end"}>
                <Grid item xs={4}>
                  <TextFieldRenderer
                    label={"Id"}
                    onChange={onChangeFilter}
                    name={"id"}
                    value={filters.id}
                    disabled={state.impersonating}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldRenderer
                    label={t("email_or_mobile")}
                    onChange={onChangeFilter}
                    name={"identity"}
                    value={filters.identity}
                    disabled={state.impersonating}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    startIcon={<PersonSearchOutlinedIcon />}
                    type="button"
                    size="small"
                    onClick={resolveUser}
                    disabled={state.loading || state.impersonating || (!filters.id.trim() && !filters.identity.trim())}
                  >
                    {t("search_user")}
                  </Button>
                </Grid>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {state.candidates.length > 1 && (
          <Box sx={{ my: 2 }} role="group" aria-label={t("impersonation_choose_account")}>
            <p>{t("impersonation_choose_account")}</p>
            <Stack spacing={1}>
              {state.candidates.map((candidate) => (
                <Box key={candidate._id} sx={{ border: "1px solid", borderColor: "divider", p: 2 }}>
                  <div>{candidate.firstName} {candidate.lastName}</div>
                  <div>Id: {candidate._id}</div>
                  <div>{candidate.mobile || "—"} · {candidate.email || "—"}</div>
                  <div>{t("clubs")}: {candidate.clubs?.map((club) => club.name).join(", ") || t("no_clubs_found")}</div>
                  <div>{t("teams")}: {candidate.teams?.map((team) => [team.club?.name, team.name].filter(Boolean).join(" / ")).join(", ") || t("no_teams_found")}</div>
                  <Button
                    onClick={() => selectCandidate(candidate)}
                    disabled={state.loading || state.impersonating}
                    aria-pressed={state.user?._id === candidate._id}
                  >
                    {t("impersonation_select_account")}
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {state.user && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction={"row"} justifyContent={"flex-end"}>
                <Button
                  startIcon={<ManageAccountsOutlinedIcon />}
                  onClick={handleImpersonate}
                  disabled={state.loading || state.impersonating || !state.user?.roles?.includes(USER_ROLES.CLUB_ADMIN)}
                >
                  Impersonate
                </Button>
              </Stack>
              <Form schema={schema(t)} values={state.user || {}} />
            </Grid>
            <Grid item xs={12}>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("clubs")}
                </AccordionSummary>
                <AccordionDetails>
                  <UserClubTable
                    data={state.user?.clubs || []}
                    emptyMessage={t("no_clubs_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("teams")}
                </AccordionSummary>
                <AccordionDetails>
                  <TeamsTable
                    data={
                      state?.user?.teams?.map((team) => ({
                        ...team,
                        userId: state.user?._id,
                      })) || []
                    }
                    emptyMessage={t("no_teams_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("user_invitations")}
                </AccordionSummary>
                <AccordionDetails>
                  <UserInvitationsTable
                    data={state?.user?.invitations || []}
                    emptyMessage={t("no_notifications_found")}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className={styles.cardContainer}>
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  {t("profiles")}
                </AccordionSummary>
                <AccordionDetails>
                  <ProfilesTable
                    data={state.profiles ? state.profiles : []}
                    emptyMessage={t("no_players_found")}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

SuperAdminImpersonate.propTypes = {};

export default SuperAdminImpersonate;
